import React, { useState, useEffect, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Disclosure, Transition } from '@headlessui/react';
// eslint-disable-next-line no-unused-vars
import { ChevronUpIcon, ChevronDownIcon, ChatBubbleLeftRightIcon, PhoneIcon, UserPlusIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import TawkToChat from '../components/TawkToChat'; // Assuming path
// import DialogflowMessenger from '../components/DialogflowMessenger'; // Or use Dialogflow if preferred
import { faqData } from './faqData'; // Import the structured FAQ data (see step 2)
import { analytics, logEvent } from '../firebase-config'; // Import analytics

/**
 * Renders the Frequently Asked Questions (FAQ) page for ProRecruit.
 * Features categorized questions in an accordion, language toggle,
 * support/sales links, chatbot integration, and a call to action.
 *
 * @component
 */
const FAQPage = () => {
    const { t, i18n } = useTranslation(['faq', 'global']); // Load 'faq' and 'global' namespaces
    const [openSection, setOpenSection] = useState(null); // Tracks the currently open accordion section ID
    const [searchTerm, setSearchTerm] = useState(''); // State for search term

    useEffect(() => {
        document.title = t('faq:metaTitle'); // Set page title using translation
          logEvent(analytics, "page_view", {
             page_title: t('faq:metaTitle'),
             page_location: window.location.href,
             page_path: window.location.pathname,
           });
        // Cleanup
        return () => {
            document.title = t('global:appName'); // Reset to default app name
        };
    }, [t]);

    // --- Language Toggle Handler ---
    const handleLanguageChange = (lang) => {
        i18n.changeLanguage(lang);
         logEvent(analytics, "select_content", { // Log language change
             content_type: "language_toggle",
             item_id: lang,
           });
    };

    // --- Filter FAQs based on search term ---
     const filteredFaqData = faqData
        .map(category => ({
            ...category,
            questions: category.questions.filter(q =>
                t(q.questionKey).toLowerCase().includes(searchTerm.toLowerCase()) ||
                t(q.answerKey).toLowerCase().includes(searchTerm.toLowerCase())
            )
        }))
        .filter(category => category.questions.length > 0); // Only show categories with matching questions


    // --- Accordion Toggle ---
     const handleAccordionClick = (questionId) => {
        const newOpenSection = openSection === questionId ? null : questionId;
        setOpenSection(newOpenSection);
        if (newOpenSection) { // Log only when opening
          logEvent(analytics, 'select_content', {
            content_type: 'faq_question',
            item_id: questionId // Use the question ID/key
          });
        }
      };

      // --- CTA Click Handler ---
      const handleCTAClick = (cta_location) => {
           logEvent(analytics, 'select_content', {
            content_type: 'cta_button',
            item_id: `faq_cta_${cta_location}` // e.g., faq_cta_signup_bottom
        });
      };

       // --- Support Link Click Handler ---
       const handleSupportLinkClick = (link_type) => {
            logEvent(analytics, 'select_content', {
             content_type: 'support_link',
             item_id: `faq_${link_type}` // e.g., faq_contact_support, faq_contact_sales
         });
       };


    return (
        <div className="bg-gradient-to-b from-gray-50 to-blue-50 min-h-screen font-sans">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">

                {/* Header Section */}
                <div className="text-center mb-12 md:mb-16 relative">
                     {/* Language Toggle - Positioned Top Right */}
                    <div className="absolute top-0 right-0 -mt-4 sm:mt-0 space-x-1 sm:space-x-2 z-10">
                        <button
                            onClick={() => handleLanguageChange('en')}
                            className={`px-2 py-1 text-xs sm:px-3 sm:py-1 sm:text-sm rounded-md transition ${i18n.language.startsWith('en') ? 'bg-[#A47713] hover:bg-[#8a6310] text-white shadow-sm' : 'bg-white text-slate-600 leading-relaxed hover:bg-gray-100'}`}
                            aria-pressed={i18n.language.startsWith('en')}
                        >
                            EN
                        </button>
                        <button
                            onClick={() => handleLanguageChange('fr')}
                            className={`px-2 py-1 text-xs sm:px-3 sm:py-1 sm:text-sm rounded-md transition ${i18n.language.startsWith('fr') ? 'bg-[#A47713] hover:bg-[#8a6310] text-white shadow-sm' : 'bg-white text-slate-600 leading-relaxed hover:bg-gray-100'}`}
                            aria-pressed={i18n.language.startsWith('fr')}
                        >
                            FR
                        </button>
                    </div>

                    <h1 className="text-4xl font-extrabold text-slate-900 font-bold tracking-tight sm:text-5xl md:text-6xl tracking-tight">
                        {t('faq:title')}
                    </h1>
                    <p className="mt-4 max-w-3xl mx-auto text-xl text-slate-500">
                        {t('faq:subtitle')}
                    </p>
                </div>

                {/* Search Bar */}
                 <div className="mb-8 max-w-2xl mx-auto">
                    <label htmlFor="faq-search" className="sr-only">{t('faq:searchPlaceholder')}</label>
                    <input
                        type="search"
                        id="faq-search"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder={t('faq:searchPlaceholder')}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 transition duration-150 ease-in-out"
                    />
                </div>

                {/* FAQ Accordion */}
                <div className="max-w-4xl mx-auto space-y-6">
                    {filteredFaqData.length > 0 ? filteredFaqData.map((category) => (
                        <div key={category.categoryKey}>
                            <h2 className="text-2xl font-semibold text-slate-900 font-bold tracking-tight mb-4 border-b pb-2 border-gray-200">
                                {t(category.categoryKey)}
                            </h2>
                            <div className="space-y-3">
                                {category.questions.map((faq) => (
                                    <Disclosure key={faq.id} as="div" className="bg-white shadow rounded-lg overflow-hidden">
                                        {({ open }) => (
                                            <>
                                                <Disclosure.Button
                                                 onClick={() => handleAccordionClick(faq.id)}
                                                 className="flex justify-between items-center w-full px-5 py-4 text-left text-lg font-medium text-blue-900 hover:bg-blue-50 focus:outline-none focus-visible:ring focus-visible:ring-blue-500 focus-visible:ring-opacity-75 transition duration-150 ease-in-out"
                                                 >
                                                    <span>{t(faq.questionKey)}</span>
                                                    <ChevronDownIcon className={`${open ? 'transform rotate-180' : ''} w-6 h-6 text-blue-500 transition-transform duration-200`} />
                                                </Disclosure.Button>
                                                <Transition
                                                    as={Fragment}
                                                    show={openSection === faq.id} // Control visibility based on state
                                                    enter="transition duration-100 ease-out"
                                                    enterFrom="transform scale-95 opacity-0"
                                                    enterTo="transform scale-100 opacity-100"
                                                    leave="transition duration-75 ease-out"
                                                    leaveFrom="transform scale-100 opacity-100"
                                                    leaveTo="transform scale-95 opacity-0"
                                                >
                                                    <Disclosure.Panel className="px-5 py-4 pt-0 text-base text-slate-600 leading-relaxed leading-relaxed">
                                                        {t(faq.answerKey)}
                                                        {/* Optionally add links from answer here if needed */}
                                                    </Disclosure.Panel>
                                                </Transition>
                                            </>
                                        )}
                                    </Disclosure>
                                ))}
                            </div>
                        </div>
                    )) : (
                         <p className="text-center text-slate-500 text-lg py-8">{t('faq:noResults')}</p>
                    )}
                </div>

                {/* Still Need Help Section */}
                <section className="mt-16 pt-12 border-t border-gray-200 text-center">
                    <h2 className="text-3xl font-bold text-slate-900 font-bold tracking-tight mb-4">
                        {t('faq:stillNeedHelpTitle')}
                    </h2>
                    <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-2xl mx-auto">
                        {t('faq:stillNeedHelpDesc')}
                    </p>
                    <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
                        {/* Chat Button - Assuming Tawk.to integration */}
                        {/* You might need Tawk_API.toggle() or similar if directly controlling */}
                        <button
                            onClick={() => { window.Tawk_API?.toggle(); handleSupportLinkClick('chat'); }} // Example toggle, adjust if using Dialogflow
                            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-[#A47713] hover:bg-[#8a6310] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-150"
                        >
                            <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2" />
                            {t('faq:chatWithUs')}
                        </button>
                         <Link
                             to="/support"
                             onClick={() => handleSupportLinkClick('contact_support')}
                            className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md shadow-sm text-slate-600 leading-relaxed bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition duration-150"
                        >
                             <PhoneIcon className="h-5 w-5 mr-2 text-slate-500" />
                            {t('faq:contactSupport')}
                         </Link>
                         <Link
                             to="/contact-sales"
                             onClick={() => handleSupportLinkClick('contact_sales')}
                             className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md shadow-sm text-slate-600 leading-relaxed bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition duration-150"
                         >
                            <UserPlusIcon className="h-5 w-5 mr-2 text-slate-500" />
                             {t('faq:contactSales')}
                         </Link>
                    </div>
                </section>

                 {/* Final CTA Section */}
                <section className="mt-16 py-12 bg-indigo-700 rounded-2xl text-center shadow-xl">
                    <h2 className="text-3xl font-extrabold text-white sm:text-4xl">
                         {t('faq:ctaTitle')}
                    </h2>
                    <p className="mt-4 text-lg leading-6 text-indigo-200 max-w-2xl mx-auto">
                         {t('faq:ctaDescription')}
                    </p>
                    <Link
                        to="/signup"
                         onClick={() => handleCTAClick('signup_bottom')}
                        className="mt-8 w-full inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-indigo-600 bg-white hover:bg-indigo-50 sm:w-auto transition duration-150 ease-in-out"
                    >
                         {t('faq:ctaButton')}
                    </Link>
                </section>

            </div>
            {/* Include Chat Widget */}
             <TawkToChat />
             {/* <DialogflowMessenger agentId="YOUR_AGENT_ID_HERE" chatTitle={t('global:appName')} /> */}
        </div>
    );
};

// No external props needed for this page component itself
FAQPage.propTypes = {};

export default FAQPage;